import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import loc from 'sources/locale/loc';
import Button from './Button';
import Spinner from './Spinner';
import styles from './BaseItemsList.scss';

const fetchTimeout = 500; // ms

function BaseItemsList(props) {
  const [state, setState] = useState({
    items: [],
    count: 0,
    offset: 0,
    error: null,
    processing: true,
    showLoadMoreButton: true,
  });

  const fetch = async clearState => {
    try {
      setState({ ...state, processing: true });

      const { onGetList, params, countPerPage } = props;
      const offset = clearState ? 0 : state.offset;

      const result = await onGetList({ limit: countPerPage, offset }, params);

      setState({
        ...state,
        processing: false,
        items: clearState
          ? [...result.items]
          : [...state.items, ...result.items],
        count: result.totalCount,
        showLoadMoreButton: clearState
          ? result.totalCount > countPerPage
          : state.showLoadMoreButton,
        offset: offset + countPerPage,
      });
    } catch (err) {
      setState({
        ...state,
        processing: false,
        error: err,
      });
    }
  };

  const { params } = props;
  const [timer, setTimer] = useState(null);
  useEffect(() => {
    if (timer) {
      clearTimeout(timer);
    }

    setTimer(setTimeout(() => fetch(true), fetchTimeout));
  }, [params]);

  const hasMore = (items, count) => {
    return items.length < count;
  };

  const handleFetchItems = force => {
    const { processing, showLoadMoreButton } = state;

    if (force) {
      fetch();
    }

    if ((hasMore() && showLoadMoreButton) || processing) {
      return;
    }

    fetch();
  };

  const handleLoadMore = () => {
    if (state.showLoadMoreButton) {
      setState({
        ...state,
        showLoadMoreButton: false,
      });
    }

    fetch();
  };

  const { processing, showLoadMoreButton, items, count } = state;
  const handleUpdateItem = item => {
    const newItems = items.map(i => {
      if (i.id === item.id) {
        return {
          ...i,
          ...item,
        };
      }

      return i;
    });
    setState({
      ...state,
      items: newItems,
    });
  };

  const { ItemsListComponent, itemsListProps, showMoreButtonClassName } = props;
  const listHasMore = hasMore(items, count);

  return (
    <div className={styles.container}>
      <ItemsListComponent
        items={items}
        count={count}
        fetched={!processing}
        {...itemsListProps}
        onFetchItems={handleFetchItems}
        onUpdateItem={handleUpdateItem}
      />

      {listHasMore && showLoadMoreButton && (
        <Button
          variant="filled"
          size="large"
          className={classnames(showMoreButtonClassName, styles.showMoreButton)}
          onClick={handleLoadMore}
          label={loc('show-more')}
        />
      )}

      {processing && <Spinner overlay />}
    </div>
  );
}

BaseItemsList.propTypes = {
  countPerPage: PropTypes.number,
  itemsListProps: PropTypes.object,
  ItemsListComponent: PropTypes.func.isRequired,
  showMoreButtonClassName: PropTypes.string,
  onGetList: PropTypes.func.isRequired,
};

BaseItemsList.defaultProps = {
  countPerPage: 10,
  itemsListProps: {},
  showMoreButtonClassName: null,
  params: {},
};

export default BaseItemsList;



// WEBPACK FOOTER //
// ./src/components/controls/BaseItemsList.jsx